import { useState, useEffect, useRef } from "react";
import { uploadAircraftData, deleteAircraftRecord, deleteAllAircraftData, lookupAircraft, getAircraftFlights, getDuplicateAircraft } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Database, Upload, Trash2, Loader2, FileSpreadsheet, Plane, AlertTriangle, Search, Pencil, Save, X, AlertCircle, CheckCircle, Download, RefreshCw, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { mergeAircraftOperator, mergeAircraftRegistrations, exportAircraftDataXlsx, getAircraftMissingInfo, getAircraftMissingList } from "@/lib/api";
import { usePermissions } from "@/context/PermissionContext";
import DeleteWithBackupDialog from "@/components/DeleteWithBackupDialog";

const MISSING_FIELDS = [
  { key: "registration", label: "Registration" },
  { key: "aircraft_type", label: "Aircraft Type" },
  { key: "aircraft_code", label: "Code" },
  { key: "operator", label: "Operator" },
  { key: "mtow", label: "MTOW (kg)" },
];

export default function AircraftDataPage() {
  const { hasPermission } = usePermissions();
  const [searchReg, setSearchReg] = useState("");
  const [searchOperator, setSearchOperator] = useState("");
  const [searchType, setSearchType] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [rowForm, setRowForm] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [inUseFlights, setInUseFlights] = useState(null);
  const [showingDuplicates, setShowingDuplicates] = useState(false);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [checkingDelete, setCheckingDelete] = useState(null);
  const [editAircraft, setEditAircraft] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [registrationCheck, setRegistrationCheck] = useState(null);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [editMissing, setEditMissing] = useState(null);
  const [deleteMissingReg, setDeleteMissingReg] = useState(null);
  const [missingForm, setMissingForm] = useState({
    registration: "",
    aircraft_type: "",
    aircraft_code: "",
    operator: "",
    mtow: 0
  });
  const [savingMissing, setSavingMissing] = useState(false);
  const fileInputRef = useRef(null);

  // Operator merge state
  const [selectedIds, setSelectedIds] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeNewName, setMergeNewName] = useState("");
  const [merging, setMerging] = useState(false);
  const [mergeRegOpen, setMergeRegOpen] = useState(false);
  const [mergeRegTarget, setMergeRegTarget] = useState("");
  const [mergeRegCustom, setMergeRegCustom] = useState("");

  // Missing info state
  const [missingInfo, setMissingInfo] = useState(null);
  const [activeMissingField, setActiveMissingField] = useState(null);

  // Permission checks
  const canAdd = hasPermission("aircraft_data", "add");
  const canEdit = hasPermission("aircraft_data", "edit");
  const canDelete = hasPermission("aircraft_data", "delete");
  const canDeleteAll = hasPermission("aircraft_data", "delete_all");
  const canUpload = hasPermission("aircraft_data", "upload");
  const canExport = hasPermission("aircraft_data", "export");
  const canMerge = hasPermission("aircraft_data", "merge");

  // Get total count and registration check on mount
  useEffect(() => {
    fetchTotalCount();
    fetchRegistrationCheck();
    fetchMissingInfo();
  }, []);

  const fetchMissingInfo = async () => {
    try {
      const res = await getAircraftMissingInfo();
      setMissingInfo(res.data);
    } catch (error) {
      // endpoint optional
    }
  };

  const handleMissingTagClick = async (field) => {
    setSearching(true);
    try {
      const res = await getAircraftMissingList(field);
      setSearchResults(res.data);
      setSelectedIds([]);
      setActiveMissingField(field);
      const label = MISSING_FIELDS.find((f) => f.key === field)?.label || field;
      if (res.data.length === 0) {
        toast.info(`No aircraft missing ${label}`);
      } else {
        toast.success(`Showing ${res.data.length} aircraft missing ${label}`);
      }
    } catch (error) {
      toast.error("Failed to load incomplete records");
    } finally {
      setSearching(false);
    }
  };

  const fetchTotalCount = async () => {
    try {
      const response = await api.get("/aircraft-data/count");
      setTotalCount(response.data.count);
    } catch {
      // count endpoint optional
    }
  };

  const fetchRegistrationCheck = async () => {
    setLoadingCheck(true);
    try {
      const response = await api.get("/flights/registration-check");
      setRegistrationCheck(response.data);
    } catch {
      // registration check optional
    } finally {
      setLoadingCheck(false);
    }
  };

  const openMissingEdit = (reg) => {
    setEditMissing(reg);
    setMissingForm({
      registration: reg.registration,
      aircraft_type: reg.aircraft_type || "",
      aircraft_code: "",
      operator: reg.operator || "",
      mtow: reg.mtow || 0
    });
  };

  const handleSaveMissing = async () => {
    if (!editMissing) return;
    
    setSavingMissing(true);
    try {
      const response = await api.post("/aircraft-data/add-missing", missingForm);
      toast.success(`${response.data.message}. ${response.data.flights_updated} flight(s) updated.`);
      setEditMissing(null);
      // Refresh the check
      fetchRegistrationCheck();
      fetchTotalCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add aircraft");
    } finally {
      setSavingMissing(false);
    }
  };

  const handleDeleteFlightsByRegistration = async () => {
    if (!deleteMissingReg) return;
    
    try {
      const response = await api.delete(`/flights/by-registration/${encodeURIComponent(deleteMissingReg)}`);
      toast.success(response.data.message);
      setDeleteMissingReg(null);
      // Refresh the check
      fetchRegistrationCheck();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete flights");
    }
  };

  const handleSearch = async () => {
    const params = new URLSearchParams();
    if (searchReg.trim()) params.append("registration", searchReg.trim().toUpperCase());
    if (searchOperator.trim()) params.append("operator", searchOperator.trim());
    if (searchType.trim()) params.append("aircraft_type", searchType.trim());
    if (searchCode.trim()) params.append("aircraft_code", searchCode.trim().toUpperCase());
    if (![...params].length) {
      toast.error("Enter at least one search field");
      return;
    }

    setSearching(true);
    setHasSearched(true);
    setShowingDuplicates(false);
    try {
      const response = await api.get(`/aircraft-data/search?${params.toString()}`);
      setSearchResults(response.data);
      setSelectedIds([]);
      setActiveMissingField(null);
      setEditingId(null);
      if (response.data.length === 0) {
        toast.info("No aircraft found matching your search");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const startInlineEdit = (a) => {
    setEditingId(a.id);
    setRowForm({
      registration: a.registration || "",
      aircraft_type: a.aircraft_type || "",
      aircraft_code: a.aircraft_code || "",
      operator: a.operator || "",
      mtow: a.mtow || 0,
    });
  };

  const cancelInlineEdit = () => {
    setEditingId(null);
    setRowForm({});
  };

  const saveInlineEdit = async (a) => {
    setSaving(true);
    try {
      const payload = { ...rowForm, mtow: parseFloat(rowForm.mtow) || 0, registration: (rowForm.registration || "").toUpperCase() };
      const res = await api.put(`/aircraft-data/${a.id}`, payload);
      toast.success(res.data?.message || "Aircraft updated & related flights synced");
      setSearchResults((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...payload } : x)));
      setEditingId(null);
      setRowForm({});
      fetchMissingInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save aircraft");
    } finally {
      setSaving(false);
    }
  };

  // Operator merge helpers
  const selectedAircraft = searchResults.filter((a) => selectedIds.includes(a.id));
  const distinctOperators = [...new Set(selectedAircraft.map((a) => (a.operator || "").trim()).filter(Boolean))];
  const allSelected = searchResults.length > 0 && selectedIds.length === searchResults.length;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : searchResults.map((a) => a.id));
  };

  const openMerge = () => {
    if (selectedIds.length < 1) {
      toast.error("Select aircraft to merge first");
      return;
    }
    setMergeTarget(distinctOperators[0] || "");
    setMergeNewName("");
    setMergeOpen(true);
  };

  const handleMerge = async () => {
    const target = (mergeNewName.trim() || mergeTarget).trim();
    if (!target) {
      toast.error("Choose an existing name or enter a new operator name");
      return;
    }
    setMerging(true);
    try {
      const res = await mergeAircraftOperator(selectedIds, target);
      toast.success(res.data.message);
      setSearchResults((prev) => prev.map((a) => (selectedIds.includes(a.id) ? { ...a, operator: target } : a)));
      setSelectedIds([]);
      setMergeOpen(false);
      fetchMissingInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  // Registration merge (for duplicate registrations)
  const sameGroup = selectedAircraft.length >= 2 && selectedAircraft.every((a) => a._dup_group && a._dup_group === selectedAircraft[0]._dup_group);

  const openMergeReg = () => {
    if (selectedAircraft.length < 2) {
      toast.error("Select at least 2 records of the same registration to merge");
      return;
    }
    if (!sameGroup) {
      toast.error("Selected records are not the same registration. Select variants of one aircraft (same duplicate group).");
      return;
    }
    // Default target: prefer a registration containing a hyphen (canonical), else the longest
    const regs = selectedAircraft.map((a) => a.registration || "").filter(Boolean);
    const withHyphen = regs.find((r) => r.includes("-"));
    setMergeRegTarget(withHyphen || regs.sort((a, b) => b.length - a.length)[0] || "");
    setMergeRegCustom("");
    setMergeRegOpen(true);
  };

  const handleMergeReg = async () => {
    const target = (mergeRegCustom.trim() || mergeRegTarget).trim().toUpperCase();
    if (!target) {
      toast.error("Choose or enter a registration to keep");
      return;
    }
    // Keeper = the selected record whose registration matches the target, else the first
    const keeper = selectedAircraft.find((a) => (a.registration || "").toUpperCase() === target) || selectedAircraft[0];
    const mergeIds = selectedAircraft.filter((a) => a.id !== keeper.id).map((a) => a.id);
    setMerging(true);
    try {
      const res = await mergeAircraftRegistrations({ keep_id: keeper.id, merge_ids: mergeIds, target_registration: target });
      toast.success(res.data.message);
      setMergeRegOpen(false);
      setSelectedIds([]);
      // Reload duplicates so the merged group disappears
      handleFindDuplicates();
      fetchTotalCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration merge failed");
    } finally {
      setMerging(false);
    }
  };


  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Export all aircraft data to XLS
  const handleExport = async () => {
    try {
      const response = await exportAircraftDataXlsx();
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `aircraft_data_${new Date().toISOString().split("T")[0]}.xlsx`;
      link.click();
      toast.success("Aircraft data exported");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
      toast.error("Please select an XLS or XLSX file");
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAircraftData(file);
      toast.success(response.data.message);
      fetchTotalCount();
      // Clear search results to force re-search
      setSearchResults([]);
      setHasSearched(false);
      setSearchReg(""); setSearchOperator(""); setSearchType(""); setSearchCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAircraftRecord(id);
      toast.success("Aircraft deleted");
      setDeleteId(null);
      // Remove from search results
      setSearchResults(prev => prev.filter(a => a.id !== id));
      fetchTotalCount();
    } catch (error) {
      if (error.response?.status === 409) {
        setDeleteId(null);
        toast.error(error.response?.data?.detail || "Aircraft is in use and cannot be deleted");
      } else {
        toast.error(error.response?.data?.detail || "Failed to delete aircraft");
      }
    }
  };

  // Before deleting, check whether the aircraft is used by any flight.
  const handleDeleteClick = async (aircraft) => {
    setCheckingDelete(aircraft.id);
    try {
      const res = await getAircraftFlights(aircraft.id);
      if ((res.data.flights || []).length > 0) {
        setInUseFlights({ registration: res.data.registration, flights: res.data.flights });
      } else {
        setDeleteId(aircraft.id);
      }
    } catch (error) {
      // Fallback: let the normal confirm handle it (backend will still block if in use)
      setDeleteId(aircraft.id);
    } finally {
      setCheckingDelete(null);
    }
  };

  const handleFindDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const res = await getDuplicateAircraft();
      setSearchResults(res.data.results || []);
      setSelectedIds([]);
      setActiveMissingField(null);
      setEditingId(null);
      setHasSearched(true);
      setShowingDuplicates(true);
      if ((res.data.results || []).length === 0) {
        toast.info("No duplicated registrations found");
      } else {
        toast.success(`Found ${res.data.group_count} duplicated registration(s) across ${res.data.total} records`);
      }
    } catch (error) {
      toast.error("Failed to load duplicates");
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      await deleteAllAircraftData();
      toast.success("All aircraft data deleted");
      setSearchResults([]);
      setTotalCount(0);
    } catch (error) {
      toast.error("Failed to delete data");
      throw error; // Re-throw for the dialog to handle
    }
  };

  const handleSaveEdit = async () => {
    if (!editAircraft) return;
    
    setSaving(true);
    try {
      if (editAircraft.id === "new") {
        // Adding new aircraft
        if (!editForm.registration) {
          toast.error("Registration is required");
          setSaving(false);
          return;
        }
        await api.post("/aircraft-data", editForm);
        toast.success("Aircraft added successfully");
        fetchTotalCount();
      } else {
        // Updating existing aircraft
        await api.put(`/aircraft-data/${editAircraft.id}`, editForm);
        toast.success("Aircraft updated");
        // Update in search results
        setSearchResults(prev => prev.map(a => 
          a.id === editAircraft.id ? { ...a, ...editForm } : a
        ));
      }
      setEditAircraft(null);
      fetchMissingInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save aircraft");
    } finally {
      setSaving(false);
    }
  };

  // Update all flights with aircraft specs from the database
  const handleUpdateAllFlightsSpecs = async () => {
    try {
      toast.loading("Updating flights with missing specs...", { id: "update-specs" });
      const response = await api.post("/api/flights/update-aircraft-specs");
      toast.success(`Updated ${response.data.updated_count} flights with missing specifications`, { id: "update-specs" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update flights specs", { id: "update-specs" });
    }
  };

  // Refresh all flights with aircraft specs (force overwrite existing)
  const handleRefreshAllSpecs = async () => {
    try {
      toast.loading("Refreshing all aircraft specifications...", { id: "refresh-specs" });
      const response = await api.post("/api/aircraft/refresh-specifications");
      toast.success(`Refreshed ${response.data.updated_count} flights with aircraft specifications (${response.data.skipped_count} skipped)`, { id: "refresh-specs" });
      // Also refresh the registration check
      fetchRegistrationCheck();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh specifications", { id: "refresh-specs" });
    }
  };

  return (
    <div className="animate-fade-in" data-testid="aircraft-data-page">
      {/* Sticky Header */}
      <div className="sticky-header-combined">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">Aircraft Data</h1>
            <p className="text-[#64748b] dark:text-gray-400 mt-1">Manage aircraft database for auto-recognition</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleRefreshAllSpecs}
              variant="outline"
              className="border-green-500 text-green-600 hover:bg-green-50"
              data-testid="refresh-specs-btn"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh Specifications
            </Button>
            <Button
              onClick={handleUpdateAllFlightsSpecs}
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
              data-testid="update-specs-btn"
            >
              <RefreshCw size={16} className="mr-2" />
              Fill Missing Specs
            </Button>
            <Button
              onClick={handleFindDuplicates}
              disabled={loadingDuplicates}
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
              data-testid="find-duplicates-btn"
            >
              {loadingDuplicates ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Copy size={16} className="mr-2" />
              )}
              Find Duplicates
            </Button>
            {canAdd && (
              <Button
                onClick={() => {
                  setEditAircraft({ id: "new" });
                  setEditForm({ registration: "", aircraft_type: "", aircraft_code: "", operator: "", mtow: "" });
                }}
                className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-semibold"
                data-testid="add-aircraft-btn"
              >
                <Database size={16} className="mr-2" />
                Add Aircraft
              </Button>
            )}
            <Badge variant="secondary" className="bg-[#f1f5f9] text-[#64748b] text-lg px-4 py-2">
              {totalCount.toLocaleString()} Aircraft
            </Badge>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <Card className="border-[#e2e8f0] mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a] text-lg">
            <Search size={20} />
            Search Aircraft
          </CardTitle>
          <CardDescription>
            Search by registration, operator, aircraft type or code — each field searches only itself
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-[#64748b] mb-1 block">Registration</Label>
              <Input
                value={searchReg}
                onChange={(e) => setSearchReg(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="e.g., OO-ABC"
                className="bg-white border-[#e2e8f0] text-[#0f172a] uppercase"
                data-testid="aircraft-search-registration"
              />
            </div>
            <div>
              <Label className="text-xs text-[#64748b] mb-1 block">Operator</Label>
              <Input
                value={searchOperator}
                onChange={(e) => setSearchOperator(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., VistaJet"
                className="bg-white border-[#e2e8f0] text-[#0f172a]"
                data-testid="aircraft-search-operator"
              />
            </div>
            <div>
              <Label className="text-xs text-[#64748b] mb-1 block">Aircraft Type</Label>
              <Input
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., Global 6000"
                className="bg-white border-[#e2e8f0] text-[#0f172a]"
                data-testid="aircraft-search-type"
              />
            </div>
            <div>
              <Label className="text-xs text-[#64748b] mb-1 block">Aircraft Code</Label>
              <Input
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="e.g., GLEX"
                className="bg-white border-[#e2e8f0] text-[#0f172a] uppercase"
                data-testid="aircraft-search-code"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-3">
            <Button
              onClick={handleSearch}
              disabled={searching}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="aircraft-search-btn"
            >
              {searching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Search size={16} className="mr-2" />
                  Search
                </>
              )}
            </Button>
            <Button
              onClick={() => { setSearchReg(""); setSearchOperator(""); setSearchType(""); setSearchCode(""); setSearchResults([]); setHasSearched(false); }}
              variant="outline"
              className="border-[#e2e8f0] text-[#64748b]"
              data-testid="aircraft-search-clear"
            >
              <X size={16} className="mr-2" />
              Clear
            </Button>
          </div>
          <p className="text-xs text-[#94a3b8] mt-2">
            Tip: You can combine fields (e.g., Operator "VistaJet" + Type "Global") for a precise match.
          </p>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card className="border-[#e2e8f0] mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a] text-lg">
            <FileSpreadsheet size={20} />
            Import Aircraft Data
          </CardTitle>
          <CardDescription>
            Upload XLS/XLSX file with columns: Registration, Type of Aircraft, Code, Operator, MTOW
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="aircraft-file-input"
            />
            {canUpload && (
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                data-testid="upload-aircraft-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Upload XLS File
                  </>
                )}
              </Button>
            )}

            {canExport && (
              <Button
                variant="outline"
                onClick={handleExport}
                className="text-[#0f172a] hover:bg-[#f1f5f9] border-[#e2e8f0]"
                data-testid="export-aircraft-btn"
              >
                <Download size={16} className="mr-2" />
                Export XLS
              </Button>
            )}

            {canDeleteAll && totalCount > 0 && (
              <Button
                variant="outline"
                onClick={() => setDeleteAllOpen(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="delete-all-aircraft-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Delete All Data
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Incomplete Records - Missing Info Tags */}
      {missingInfo && missingInfo.any > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-amber-700 dark:text-amber-400 text-lg">
              <AlertCircle size={20} />
              Incomplete Aircraft Records ({missingInfo.any})
            </CardTitle>
            <CardDescription className="text-amber-600 dark:text-amber-500">
              Click a field below to view and edit aircraft that are missing that information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {MISSING_FIELDS.map((f) => {
                const count = missingInfo[f.key] || 0;
                const isActive = activeMissingField === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => handleMissingTagClick(f.key)}
                    disabled={count === 0}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      count === 0
                        ? "opacity-40 cursor-not-allowed border-[#e2e8f0] text-[#94a3b8]"
                        : isActive
                          ? "bg-amber-500 text-white border-amber-500"
                          : "bg-white dark:bg-[#1e293b] text-amber-700 dark:text-amber-300 border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                    }`}
                    data-testid={`missing-tag-${f.key}`}
                  >
                    {f.label}
                    <span className={`inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full text-xs font-bold ${
                      isActive ? "bg-white/25 text-white" : "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registration Check - Missing Aircraft */}
      {registrationCheck && registrationCheck.missing_from_database > 0 && (
        <Card className="border-red-200 bg-red-50 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-red-700 text-lg">
              <AlertCircle size={20} />
              Missing Aircraft Data ({registrationCheck.missing_from_database})
            </CardTitle>
            <CardDescription className="text-red-600">
              Add aircraft data or delete flights with these registrations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {registrationCheck.registrations
                .filter(r => r.status === "missing")
                .map((reg, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-red-200"
                  >
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle size={18} className="text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-mono font-bold text-red-700">{reg.registration}</p>
                      <p className="text-xs text-red-500">Flight: {reg.sample_flight}</p>
                    </div>
                    <div className="flex gap-1">
                      {canAdd && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openMissingEdit(reg)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Add to database"
                        >
                          <Pencil size={16} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteMissingReg(reg.registration)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                          title="Delete all flights with this registration"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
            <p className="text-xs text-red-500 mt-3">
              💡 Use ✏️ to add aircraft data or 🗑️ to delete all flights with that registration
            </p>
          </CardContent>
        </Card>
      )}

      {/* Registration Check Summary */}
      {registrationCheck && (
        <Card className="border-[#e2e8f0] mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a] text-lg">
              <Database size={20} />
              Registration Check Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#f1f5f9] rounded-lg">
                <Plane size={18} className="text-[#64748b]" />
                <span className="text-sm text-[#64748b]">Total Registrations:</span>
                <span className="font-bold text-[#0f172a]">{registrationCheck.total_registrations}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                <CheckCircle size={18} className="text-green-600" />
                <span className="text-sm text-green-600">Found in Database:</span>
                <span className="font-bold text-green-700">{registrationCheck.found_in_database}</span>
              </div>
              {registrationCheck.missing_from_database > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg">
                  <AlertCircle size={18} className="text-red-600" />
                  <span className="text-sm text-red-600">Missing:</span>
                  <span className="font-bold text-red-700">{registrationCheck.missing_from_database}</span>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRegistrationCheck}
              disabled={loadingCheck}
              className="mt-3"
            >
              {loadingCheck ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Refresh Check
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Search Results Table */}
      {searchResults.length > 0 && (
        <Card className="border-[#e2e8f0] mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-lg font-['Outfit'] text-[#0f172a]">
                {showingDuplicates
                  ? `Duplicated Registrations (${searchResults.length})`
                  : activeMissingField
                  ? `Incomplete — Missing ${MISSING_FIELDS.find((f) => f.key === activeMissingField)?.label} (${searchResults.length})`
                  : `Search Results (${searchResults.length})`}
              </CardTitle>
              <div className="flex items-center gap-3">
                {selectedIds.length > 0 && (
                  <span className="text-sm text-[#64748b]" data-testid="selected-count">
                    {selectedIds.length} selected
                  </span>
                )}
                {canMerge && (
                  <Button
                    onClick={openMerge}
                    disabled={selectedIds.length < 1}
                    variant="outline"
                    className="border-[#d4af37] text-[#b5952f] hover:bg-[#d4af37]/10"
                    data-testid="merge-operator-btn"
                  >
                    <Users size={16} className="mr-2" />
                    Merge Operator
                  </Button>
                )}
                {canMerge && showingDuplicates && (
                  <Button
                    onClick={openMergeReg}
                    disabled={selectedIds.length < 2}
                    variant="outline"
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                    data-testid="merge-registration-btn"
                  >
                    <Copy size={16} className="mr-2" />
                    Merge Registrations
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#0f172a] hover:bg-[#0f172a]">
                    {canMerge && (
                      <TableHead className="text-white font-semibold w-10">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleSelectAll}
                          className="border-white data-[state=checked]:bg-[#d4af37] data-[state=checked]:border-[#d4af37]"
                          data-testid="select-all-aircraft"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-white font-semibold">Registration</TableHead>
                    <TableHead className="text-white font-semibold">Aircraft Type</TableHead>
                    <TableHead className="text-white font-semibold">Code</TableHead>
                    <TableHead className="text-white font-semibold">Operator</TableHead>
                    <TableHead className="text-white font-semibold">MTOW (kg)</TableHead>
                    <TableHead className="text-white font-semibold w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchResults.map((aircraft, index) => (
                    <TableRow 
                      key={aircraft.id || index} 
                      className="hover:bg-[#f8fafc]"
                      data-testid={`aircraft-row-${index}`}
                    >
                      {canMerge && (
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(aircraft.id)}
                            onCheckedChange={() => toggleSelect(aircraft.id)}
                            data-testid={`select-aircraft-${aircraft.id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        {editingId === aircraft.id ? (
                          <Input
                            value={rowForm.registration}
                            onChange={(e) => setRowForm({ ...rowForm, registration: e.target.value.toUpperCase() })}
                            className="h-8 font-mono uppercase"
                            data-testid={`inline-registration-${aircraft.id}`}
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#0f172a] rounded flex items-center justify-center">
                              <Plane size={14} className="text-[#d4af37]" />
                            </div>
                            <span className="font-mono font-semibold text-[#0f172a]">
                              {aircraft.registration}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-[#0f172a]">
                        {editingId === aircraft.id ? (
                          <Input
                            value={rowForm.aircraft_type}
                            onChange={(e) => setRowForm({ ...rowForm, aircraft_type: e.target.value })}
                            className="h-8"
                            data-testid={`inline-type-${aircraft.id}`}
                          />
                        ) : (aircraft.aircraft_type || "-")}
                      </TableCell>
                      <TableCell>
                        {editingId === aircraft.id ? (
                          <Input
                            value={rowForm.aircraft_code}
                            onChange={(e) => setRowForm({ ...rowForm, aircraft_code: e.target.value.toUpperCase() })}
                            className="h-8 font-mono uppercase w-24"
                            data-testid={`inline-code-${aircraft.id}`}
                          />
                        ) : (
                          <Badge variant="secondary" className="bg-[#f1f5f9] text-[#64748b] font-mono">
                            {aircraft.aircraft_code || "-"}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-[#0f172a]">
                        {editingId === aircraft.id ? (
                          <Input
                            value={rowForm.operator}
                            onChange={(e) => setRowForm({ ...rowForm, operator: e.target.value })}
                            className="h-8"
                            data-testid={`inline-operator-${aircraft.id}`}
                          />
                        ) : (aircraft.operator || "-")}
                      </TableCell>
                      <TableCell className="text-[#0f172a] font-mono">
                        {editingId === aircraft.id ? (
                          <Input
                            type="number"
                            value={rowForm.mtow}
                            onChange={(e) => setRowForm({ ...rowForm, mtow: e.target.value })}
                            className="h-8 font-mono w-28"
                            data-testid={`inline-mtow-${aircraft.id}`}
                          />
                        ) : (aircraft.mtow || "-")}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {editingId === aircraft.id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => saveInlineEdit(aircraft)}
                                disabled={saving}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`save-inline-aircraft-${aircraft.id}`}
                              >
                                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={cancelInlineEdit}
                                className="text-[#64748b] hover:text-red-600 hover:bg-red-50"
                                data-testid={`cancel-inline-aircraft-${aircraft.id}`}
                              >
                                <X size={16} />
                              </Button>
                            </>
                          ) : (
                            <>
                              {canEdit && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => startInlineEdit(aircraft)}
                                  className="text-[#64748b] hover:text-[#0f172a] hover:bg-[#f1f5f9]"
                                  data-testid={`edit-aircraft-${aircraft.id}`}
                                >
                                  <Pencil size={16} />
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteClick(aircraft)}
                                  disabled={checkingDelete === aircraft.id}
                                  className="text-[#64748b] hover:text-red-600 hover:bg-red-50"
                                  data-testid={`delete-aircraft-${aircraft.id}`}
                                >
                                  {checkingDelete === aircraft.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {searchResults.length === 0 && !hasSearched && (
        <Card className="border-[#e2e8f0]">
          <CardContent className="py-12">
            <div className="text-center text-[#64748b]">
              <Database size={48} className="mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold text-[#0f172a] mb-2">Search for Aircraft</h3>
              <p>Enter a registration number above to search the database</p>
              <p className="text-sm mt-2">Database contains {totalCount.toLocaleString()} aircraft records</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage Info */}
      <Card className="border-[#e2e8f0] mt-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#d4af37] rounded-lg flex items-center justify-center flex-shrink-0">
              <Plane size={20} className="text-[#0f172a]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0f172a] mb-1">Auto-Recognition Feature</h3>
              <p className="text-sm text-[#64748b]">
                When creating a new flight, entering a registration from this database will automatically 
                fill in the Aircraft Type, Operator, and MTOW fields.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={!!editAircraft} onOpenChange={() => setEditAircraft(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] font-['Outfit']">
              {editAircraft?.id === "new" ? "Add New Aircraft" : `Edit Aircraft - ${editForm.registration}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#0f172a]">Registration *</Label>
              <Input
                value={editForm.registration}
                onChange={(e) => setEditForm({ ...editForm, registration: e.target.value.toUpperCase() })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., OO-ABC, N12345"
                data-testid="edit-registration-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0f172a]">Aircraft Type</Label>
              <Input
                value={editForm.aircraft_type}
                onChange={(e) => setEditForm({ ...editForm, aircraft_type: e.target.value })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., Gulfstream G650"
                data-testid="edit-type-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0f172a]">ICAO Code</Label>
              <Input
                value={editForm.aircraft_code}
                onChange={(e) => setEditForm({ ...editForm, aircraft_code: e.target.value.toUpperCase() })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., GLF6"
                data-testid="edit-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0f172a]">Operator</Label>
              <Input
                value={editForm.operator}
                onChange={(e) => setEditForm({ ...editForm, operator: e.target.value })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., VistaJet"
                data-testid="edit-operator-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0f172a]">MTOW (kg)</Label>
              <Input
                type="number"
                value={editForm.mtow}
                onChange={(e) => setEditForm({ ...editForm, mtow: parseFloat(e.target.value) || 0 })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., 45178"
                data-testid="edit-mtow-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditAircraft(null)}
              className="border-[#e2e8f0]"
            >
              <X size={16} className="mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="save-aircraft-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Single Aircraft Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a]">Delete Aircraft</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this aircraft record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Aircraft In Use — cannot delete, must edit flights first */}
      <Dialog open={!!inUseFlights} onOpenChange={() => setInUseFlights(null)}>
        <DialogContent className="bg-white max-w-3xl" data-testid="aircraft-inuse-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-500" />
              Cannot Delete — Aircraft In Use
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#475569]">
              <span className="font-mono font-semibold">{inUseFlights?.registration}</span> is used by{" "}
              <span className="font-semibold">{inUseFlights?.flights?.length || 0}</span> flight movement(s).
              Please edit or reassign these flights first, then you can delete the aircraft.
            </p>
            <div className="max-h-80 overflow-auto border border-[#e2e8f0] rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Flight</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(inUseFlights?.flights || []).map((f, i) => (
                    <TableRow key={f.id || i} data-testid={`inuse-flight-row-${i}`}>
                      <TableCell className="text-[#0f172a] whitespace-nowrap">{f.flight_date || "-"}</TableCell>
                      <TableCell className="font-mono text-[#0f172a]">
                        {(f.origin || "?")} → {(f.destination || "?")}
                      </TableCell>
                      <TableCell className="text-[#475569]">{f.flight_number || f.callsign || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={f._collection === "canceled_flights" ? "bg-red-100 text-red-700" : "bg-[#f1f5f9] text-[#64748b]"}>
                          {f._collection === "canceled_flights" ? "Canceled" : (f.status || "active")}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setInUseFlights(null)}
              className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
              data-testid="inuse-dialog-close"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete All Dialog with Backup */}
      <DeleteWithBackupDialog
        open={deleteAllOpen}
        onOpenChange={setDeleteAllOpen}
        title="Delete All Aircraft Data"
        description="Are you sure you want to delete ALL aircraft records? This action cannot be undone."
        itemCount={totalCount}
        onConfirmDelete={handleDeleteAll}
        leavePath="/dashboard"
      />

      {/* Add Missing Aircraft Dialog */}
      <Dialog open={!!editMissing} onOpenChange={() => setEditMissing(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] flex items-center gap-2">
              <Plane size={20} className="text-[#d4af37]" />
              Add Missing Aircraft
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium">This will:</p>
              <ul className="list-disc list-inside mt-1 text-xs">
                <li>Add the aircraft to your database</li>
                <li>Update all flights with this registration</li>
              </ul>
            </div>
            <div>
              <Label className="text-[#0f172a]">Registration</Label>
              <Input
                value={missingForm.registration}
                disabled
                className="bg-gray-100 border-[#e2e8f0] font-mono font-bold"
              />
            </div>
            <div>
              <Label className="text-[#0f172a]">Aircraft Type *</Label>
              <Input
                value={missingForm.aircraft_type}
                onChange={(e) => setMissingForm({ ...missingForm, aircraft_type: e.target.value })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., Cessna Citation XLS+"
                data-testid="missing-type-input"
              />
            </div>
            <div>
              <Label className="text-[#0f172a]">Aircraft Code</Label>
              <Input
                value={missingForm.aircraft_code}
                onChange={(e) => setMissingForm({ ...missingForm, aircraft_code: e.target.value.toUpperCase() })}
                className="bg-white border-[#e2e8f0] uppercase"
                placeholder="e.g., C56X"
                data-testid="missing-code-input"
              />
            </div>
            <div>
              <Label className="text-[#0f172a]">Operator</Label>
              <Input
                value={missingForm.operator}
                onChange={(e) => setMissingForm({ ...missingForm, operator: e.target.value })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., ASL Airlines Belgium"
                data-testid="missing-operator-input"
              />
            </div>
            <div>
              <Label className="text-[#0f172a]">MTOW (kg)</Label>
              <Input
                type="number"
                value={missingForm.mtow}
                onChange={(e) => setMissingForm({ ...missingForm, mtow: parseFloat(e.target.value) || 0 })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., 9163"
                data-testid="missing-mtow-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditMissing(null)}
              className="border-[#e2e8f0]"
            >
              <X size={16} className="mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveMissing}
              disabled={savingMissing || !missingForm.aircraft_type}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="save-missing-btn"
            >
              {savingMissing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Add & Update Flights
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Flights by Registration Dialog */}
      <AlertDialog open={!!deleteMissingReg} onOpenChange={() => setDeleteMissingReg(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={20} />
              Delete All Flights with Registration
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete ALL flights with registration <span className="font-mono font-bold text-red-600">{deleteMissingReg}</span>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFlightsByRegistration}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete All Flights
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Merge Operator Dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] font-['Outfit'] flex items-center gap-2">
              <Users size={20} className="text-[#d4af37]" />
              Merge Operator
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#64748b]">
              Apply one operator name to the <span className="font-semibold text-[#0f172a]">{selectedIds.length}</span> selected aircraft (and their flights).
            </p>
            {distinctOperators.length > 0 && (
              <div className="space-y-2">
                <Label className="text-[#0f172a]">Choose an existing spelling</Label>
                <div className="space-y-1.5">
                  {distinctOperators.map((op) => {
                    const cnt = selectedAircraft.filter((a) => (a.operator || "").trim() === op).length;
                    const checked = !mergeNewName.trim() && mergeTarget === op;
                    return (
                      <label
                        key={op}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${checked ? "border-[#d4af37] bg-[#d4af37]/10" : "border-[#e2e8f0]"}`}
                        data-testid={`merge-option-${op}`}
                      >
                        <input
                          type="radio"
                          name="merge-operator-choice"
                          checked={checked}
                          onChange={() => { setMergeTarget(op); setMergeNewName(""); }}
                          className="accent-[#d4af37]"
                        />
                        <span className="flex-1 text-sm text-[#0f172a]">{op}</span>
                        <Badge variant="secondary" className="bg-[#f1f5f9] text-[#64748b]">{cnt}</Badge>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#0f172a]">Or enter a new name</Label>
              <Input
                value={mergeNewName}
                onChange={(e) => setMergeNewName(e.target.value)}
                placeholder="e.g., VistaJet"
                className="bg-white border-[#e2e8f0]"
                data-testid="merge-new-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)} className="border-[#e2e8f0]">
              <X size={16} className="mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleMerge}
              disabled={merging || (!mergeNewName.trim() && !mergeTarget)}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="confirm-merge-btn"
            >
              {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Users size={16} className="mr-2" />Merge</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Registrations Dialog */}
      <Dialog open={mergeRegOpen} onOpenChange={setMergeRegOpen}>
        <DialogContent className="bg-white" data-testid="merge-registration-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] flex items-center gap-2">
              <Copy size={18} className="text-amber-500" />
              Merge Duplicate Registrations
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#475569]">
              Combine the <span className="font-semibold text-[#0f172a]">{selectedAircraft.length}</span> selected records into one.
              Choose the registration to <span className="font-semibold">keep</span>; the others are deleted and all their flights are reassigned to the kept registration.
            </p>
            <div>
              <Label className="text-[#0f172a] mb-2 block">Keep this registration</Label>
              <div className="space-y-2 max-h-48 overflow-auto">
                {selectedAircraft.map((a) => {
                  const checked = !mergeRegCustom.trim() && (a.registration || "").toUpperCase() === (mergeRegTarget || "").toUpperCase();
                  return (
                    <label key={a.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${checked ? "border-amber-500 bg-amber-50" : "border-[#e2e8f0]"}`} data-testid={`merge-reg-option-${a.id}`}>
                      <input
                        type="radio"
                        name="merge-reg-choice"
                        checked={checked}
                        onChange={() => { setMergeRegTarget(a.registration || ""); setMergeRegCustom(""); }}
                        className="accent-amber-500"
                      />
                      <span className="font-mono font-semibold text-[#0f172a]">{a.registration || "(blank)"}</span>
                      <span className="text-xs text-[#64748b]">{a.aircraft_type || "-"} · {a.operator || "-"}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-[#0f172a]">Or enter a corrected registration</Label>
              <Input
                value={mergeRegCustom}
                onChange={(e) => setMergeRegCustom(e.target.value.toUpperCase())}
                placeholder="e.g., D-AMGL"
                className="bg-white border-[#e2e8f0] font-mono uppercase"
                data-testid="merge-reg-custom-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeRegOpen(false)} className="border-[#e2e8f0]">
              <X size={16} className="mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleMergeReg}
              disabled={merging || (!mergeRegCustom.trim() && !mergeRegTarget)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="confirm-merge-reg-btn"
            >
              {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Copy size={16} className="mr-2" />Merge</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
